// src/content/insights/perceptual-ground-truth.jsx

import Figure from "../../../components/insights/Figure";
import FigureRef from "../../../components/insights/FigureRef";
import Quote from "../../../components/insights/Quote";
import ExternalLink from "../../../components/insights/ExternalLink";

export const article = {
  slug: "perceptual-ground-truth",

  meta: {
    title:
      "Human Expertise as Infrastructure for Voice AI",

    publishedAt: "2026-05-18",

    category: "AI infrastructure",

    author: [
      {
        name: "Neus Calaf",
        role: "Founder, All-Voiced",
      },
    ],

    excerpt:
      "Reflections after presenting All-Voiced at the IEEE SPS Entrepreneurship Forum during ICASSP 2026 in Barcelona, where the project was introduced as a human-centered perceptual infrastructure for voice AI.",

    coverImage:
      "/images/insights/2026/perceptual-ground-truth/icassp-stage.png",

    references: {},
  },

  tags: [
    "ICASSP",
    "voice-ai",
    "human-centered-ai",
    "perceptual-evaluation",
    "infrastructure",
  ],

  related: [],

  wordCount: 805,

  Component: function Article() {
    return (
      <>
        <p>
          On May 4, 2026, I presented All-Voiced at the IEEE Signal Processing 
          Society Entrepreneurship Forum during <ExternalLink href="https://2026.ieeeicassp.org/">ICASSP 2026
          </ExternalLink> — the IEEE International 
          Conference on Acoustics, Speech, and Signal Processing — held in Barcelona, 
          after being selected as one of the finalists of the forum’s startup competition.
        </p>

        <p>
          The forum focused on deep tech innovation, artificial intelligence,
          and the translation of research into scalable real-world systems.
          Most projects were framed around AI models, signal processing,
          multimodal systems, or applied machine learning.
        </p>

        <p>
          All-Voiced entered that space from a different angle.
        </p>

        <Quote>
          One of the missing layers in voice AI may not be another model, but
          better structured human perception.
        </Quote>

        <p>
          That was the central idea behind the presentation.
        </p>

        <p>
          Rather than positioning All-Voiced as a voice app or a training
          platform, the pitch explored a broader infrastructural hypothesis:
          auditory-perceptual evaluation could become a structured,
          scalable, reusable layer connecting voice science, education,
          clinical practice, and AI-related ecosystems.
        </p>

        <h2>
          Voice AI depends on perceptual judgments
        </h2>

        <p>
          Many voice-related AI systems ultimately depend on human perceptual
          interpretation.
        </p>

        <p>
          Models are trained, validated, or benchmarked against perceptual
          labels generated by human evaluators:
          overall severity,
          roughness,
          breathiness,
          strain,
          or other clinically meaningful perceptual dimensions.
        </p>

        <p>
          But despite their importance, these perceptual workflows often remain
          fragmented, weakly calibrated, difficult to scale, and operationally
          invisible.
        </p>

        <p>
          Perceptual expertise exists.
          Yet the infrastructures required to structure, monitor, calibrate,
          and reuse that expertise at scale are still limited.
        </p>

        <p>
          This framing became central to the presentation:
          the problem is not only generating labels.
          The problem is generating trustworthy perceptual ground truth.
        </p>

        <h2>
          Human expertise as infrastructure
        </h2>

        <p>
          One of the ideas I wanted to communicate during the forum was that
          perceptual expertise should not be treated as an isolated or informal
          layer surrounding AI systems.
        </p>

        <p>
          Human expertise itself can function as infrastructure.
        </p>

        <p>
          Calibration systems,
          benchmarking workflows,
          structured annotation environments,
          evaluator monitoring,
          multirater comparison,
          and reusable perceptual datasets
          are not peripheral elements.
          They are part of the core architecture required to generate
          meaningful human-centered AI ecosystems.
        </p>

        <Quote>
          Human expertise is not only input for AI systems. It can also become a
          structured infrastructure layer.
        </Quote>

        <p>
          This perspective also changes how perceptual evaluation is understood.
        </p>

        <p>
          Instead of isolated listening sessions or disconnected annotations,
          perceptual evaluation starts to function as:
        </p>

        <ul>
          <li>structured data generation,</li>
          <li>distributed evaluator coordination,</li>
          <li>calibration infrastructure,</li>
          <li>benchmarking infrastructure,</li>
          <li>and longitudinal human-centered validation.</li>
        </ul>

        <p>
          Presenting these ideas at ICASSP helped clarify a broader question:
          what kinds of human-centered infrastructures future voice AI systems may ultimately depend on.
        </p>

        <h2>
          Beyond training platforms
        </h2>

        <p>
          Historically, auditory-perceptual voice evaluation has often been
          approached primarily as a pedagogical or clinical challenge:
          how to train evaluators,
          improve consistency,
          or teach listening skills more effectively.
        </p>

        <p>
          Those dimensions remain essential.
          But during the preparation of the ICASSP pitch, it became increasingly
          clear that the deeper opportunity might be infrastructural rather than
          purely educational.
        </p>

        <p>
          If perceptual evaluation workflows become structured, monitorable, and
          reusable, they can begin to support:
        </p>

        <ul>
          <li>research interoperability,</li>
          <li>multicenter perceptual studies,</li>
          <li>cross-cultural evaluation systems,</li>
          <li>AI-compatible annotation pipelines,</li>
          <li>and large-scale perceptual datasets.</li>
        </ul>

        <p>
          This infrastructural framing became central to the presentation, as
          illustrated in <FigureRef id="fig2" />.
        </p>

        <Figure
          id="fig2"
          variant="slide"
          src="/images/insights/2026/perceptual-ground-truth/perceptual-ground-truth-slide.png"
          caption="Conceptual framing presented during the ICASSP 2026 pitch: perceptual ground truth as a foundational layer for AI systems."
        />

        <p>
          The pitch therefore became less about presenting a product and more
          about proposing a possible infrastructural direction for voice AI:
        </p>

        <h2>
          Translating voice science into AI language
        </h2>

        <p>
          One of the most valuable aspects of the experience was the need to
          translate concepts from voice science into a language understandable
          within AI and signal processing ecosystems.
        </p>

        <p>
          Terms like:
        </p>

        <ul>
          <li>perceptual calibration,</li>
          <li>evaluator reliability,</li>
          <li>human-in-the-loop evaluation,</li>
          <li>structured annotation workflows,</li>
          <li>and perceptual ground truth</li>
        </ul>

        <p>
          suddenly became bridges between disciplines that do not usually speak
          to each other directly.
        </p>

        <p>
          That translation process was intellectually important.
        </p>

        <p>
          It revealed that many of the challenges long recognized within
          auditory-perceptual voice evaluation were not isolated educational or
          clinical issues, but infrastructure problems with broader relevance
          for AI systems that increasingly depend on human interpretation.
        </p>

        <Quote>
          The future of voice AI may depend not only on better models, but also
          on better infrastructures for human perception.
        </Quote>

        <h2>
          What the forum clarified
        </h2>

        <p>
          More than a startup milestone, the experience helped crystallize a broader
          infrastructural framing for All-Voiced.
        </p>

        <p>
          It was one of the first moments where All-Voiced was publicly framed not only
          as a platform for perceptual training, but as a possible infrastructure for
          scalable human-centered evaluation.
        </p>

        <p>
          The forum helped clarify something fundamental:
          listening itself can become structured scientific infrastructure.
        </p>
      </>
    );
  },
};